import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  FileText,
  FilePlus,
  Library,
  LogOut,
  Save,
  Signature,
  Workflow,
} from 'lucide-react';
import { logout } from '@/auth/cognito';
import { Button } from '@/components/Button';
import { DocumentsPane } from './DocumentsPane';
import { TemplatesPane } from './TemplatesPane';
import { ClausesPane } from './ClausesPane';
import { SavePane } from './SavePane';

type View = 'home' | 'documents' | 'templates' | 'clauses' | 'save';

const tiles = [
  { id: 'documents' as const, icon: FileText, key: 'home.documents', desc: 'home.documentsDesc' },
  { id: 'templates' as const, icon: FilePlus, key: 'home.templates', desc: 'home.templatesDesc' },
  { id: 'clauses' as const, icon: Library, key: 'home.clauses', desc: 'home.clausesDesc' },
  { id: 'save' as const, icon: Save, key: 'home.save', desc: 'home.saveDesc' },
];

export function HomePane() {
  const { t } = useTranslation();
  const [view, setView] = useState<View>('home');

  if (view !== 'home') {
    return (
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between border-b border-lex-slate-200 px-4 py-2">
          <button
            type="button"
            onClick={() => setView('home')}
            className="flex items-center gap-1 text-xs text-lex-slate-500 hover:text-lex-slate-900"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {t('common.back')}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {view === 'documents' && <DocumentsPane />}
          {view === 'templates' && <TemplatesPane />}
          {view === 'clauses' && <ClausesPane />}
          {view === 'save' && <SavePane />}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="grid grid-cols-1 gap-2 p-4">
        {tiles.map(({ id, icon: Icon, key, desc }) => (
          <button
            key={id}
            type="button"
            onClick={() => setView(id)}
            className="flex items-start gap-3 rounded-lg border border-lex-slate-200 bg-white p-3 text-left transition hover:border-lex-magenta/40 hover:bg-lex-slate-50"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-lex-magenta/10 text-lex-magenta">
              <Icon className="h-4 w-4" />
            </span>
            <span className="space-y-0.5">
              <span className="block text-sm font-medium">{t(key)}</span>
              <span className="block text-xs text-lex-slate-500">{t(desc)}</span>
            </span>
          </button>
        ))}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            disabled
            className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-lex-slate-200 p-3 text-xs text-lex-slate-400"
          >
            <Signature className="h-3.5 w-3.5" />
            {t('home.sign')}
          </button>
          <button
            type="button"
            disabled
            className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-lex-slate-200 p-3 text-xs text-lex-slate-400"
          >
            <Workflow className="h-3.5 w-3.5" />
            {t('home.workflow')}
          </button>
        </div>
      </div>
      <div className="mt-auto border-t border-lex-slate-200 p-3">
        <Button variant="ghost" size="sm" onClick={logout} className="w-full">
          <LogOut className="mr-2 h-3.5 w-3.5" />
          {t('auth.logout')}
        </Button>
      </div>
    </div>
  );
}
